import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// gloabal types
import {
  AvailabilityStatus,
  FridaySchedule,
  MondaySchedule,
  SaturdaySchedule,
  ScheduleData,
  SundaySchedule,
  ThursdaySchedule,
  TimeFormat,
  TuesdaySchedule,
  WednesdaySchedule
} from '../types/scheduleTypes'

// helpers
import { delayHttpRequest } from '../helpers/globalHelper'

// services
import { scheduleService } from '../services/scheduleService'

// stores
import { useHomeStore } from './home'

// types
type Store = {
  scheduleData: ScheduleData
  loading: boolean
  error: string | null
  clearData: () => void
  setRequestedHours: (hours: number) => void
  setSchedulingComment: (note: string) => void
  getSchedule(
    day: number
  ):
    | MondaySchedule
    | TuesdaySchedule
    | WednesdaySchedule
    | ThursdaySchedule
    | FridaySchedule
    | SaturdaySchedule
    | SundaySchedule
  setSchedule: (
    day: number,
    status: AvailabilityStatus,
    note: string,
    timeOneStart: TimeFormat,
    timeOneEnd: TimeFormat,
    timeTwoStart: TimeFormat,
    timeTwoEnd: TimeFormat
  ) => void
  fetchData: (id: number) => void
  updateData: (id: number, feed: ScheduleData) => void
}

const useScheduleStore = create(
  devtools<Store>((set) => ({
    scheduleData: null,
    loading: false,
    error: null,
    clearData: () => set({ scheduleData: null, loading: false, error: null }),
    setRequestedHours: (hours: number) => {
      set((state) => ({
        ...state,
        scheduleData: {
          ...useScheduleStore.getState().scheduleData,
          requestedHours: hours
        }
      }))
    },
    setSchedulingComment: (note) => {
      set((state) => ({
        ...state,
        scheduleData: {
          ...useScheduleStore.getState().scheduleData,
          schedulingComment: note?.trim() || null
        }
      }))
    },
    getSchedule: (day) => {
      let scheduleDayObj = null

      if (day === 1) {
        scheduleDayObj = {
          mondayScheduleValue:
            useScheduleStore.getState().scheduleData?.mondayScheduleValue,
          mondayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.mondayScheduleNAComment,
          mondayScheduleTime1Start:
            useScheduleStore.getState().scheduleData?.mondayScheduleTime1Start,
          mondayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.mondayScheduleTime1End,
          mondayScheduleTime2Start:
            useScheduleStore.getState().scheduleData?.mondayScheduleTime2Start,
          mondayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.mondayScheduleTime2End
        }
      } else if (day === 2) {
        scheduleDayObj = {
          tuesdayScheduleValue:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleValue,
          tuesdayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleNAComment,
          tuesdayScheduleTime1Start:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleTime1Start,
          tuesdayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleTime1End,
          tuesdayScheduleTime2Start:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleTime2Start,
          tuesdayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.tuesdayScheduleTime2End
        }
      } else if (day === 3) {
        scheduleDayObj = {
          wednesdayScheduleValue:
            useScheduleStore.getState().scheduleData?.wednesdayScheduleValue,
          wednesdayScheduleNAComment:
            useScheduleStore.getState().scheduleData
              ?.wednesdayScheduleNAComment,
          wednesdayScheduleTime1Start:
            useScheduleStore.getState().scheduleData
              ?.wednesdayScheduleTime1Start,
          wednesdayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.wednesdayScheduleTime1End,
          wednesdayScheduleTime2Start:
            useScheduleStore.getState().scheduleData
              ?.wednesdayScheduleTime2Start,
          wednesdayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.wednesdayScheduleTime2End
        }
      } else if (day === 4) {
        scheduleDayObj = {
          thursdayScheduleValue:
            useScheduleStore.getState().scheduleData?.thursdayScheduleValue,
          thursdayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.thursdayScheduleNAComment,
          thursdayScheduleTime1Start:
            useScheduleStore.getState().scheduleData
              ?.thursdayScheduleTime1Start,
          thursdayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.thursdayScheduleTime1End,
          thursdayScheduleTime2Start:
            useScheduleStore.getState().scheduleData
              ?.thursdayScheduleTime2Start,
          thursdayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.thursdayScheduleTime2End
        }
      } else if (day === 5) {
        scheduleDayObj = {
          fridayScheduleValue:
            useScheduleStore.getState().scheduleData?.fridayScheduleValue,
          fridayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.fridayScheduleNAComment,
          fridayScheduleTime1Start:
            useScheduleStore.getState().scheduleData?.fridayScheduleTime1Start,
          fridayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.fridayScheduleTime1End,
          fridayScheduleTime2Start:
            useScheduleStore.getState().scheduleData?.fridayScheduleTime2Start,
          fridayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.fridayScheduleTime2End
        }
      } else if (day === 6) {
        scheduleDayObj = {
          saturdayScheduleValue:
            useScheduleStore.getState().scheduleData?.saturdayScheduleValue,
          saturdayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.saturdayScheduleNAComment,
          saturdayScheduleTime1Start:
            useScheduleStore.getState().scheduleData
              ?.saturdayScheduleTime1Start,
          saturdayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.saturdayScheduleTime1End,
          saturdayScheduleTime2Start:
            useScheduleStore.getState().scheduleData
              ?.saturdayScheduleTime2Start,
          saturdayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.saturdayScheduleTime2End
        }
      } else if (day === 0) {
        scheduleDayObj = {
          sundayScheduleValue:
            useScheduleStore.getState().scheduleData?.sundayScheduleValue,
          sundayScheduleNAComment:
            useScheduleStore.getState().scheduleData?.sundayScheduleNAComment,
          sundayScheduleTime1Start:
            useScheduleStore.getState().scheduleData?.sundayScheduleTime1Start,
          sundayScheduleTime1End:
            useScheduleStore.getState().scheduleData?.sundayScheduleTime1End,
          sundayScheduleTime2Start:
            useScheduleStore.getState().scheduleData?.sundayScheduleTime2Start,
          sundayScheduleTime2End:
            useScheduleStore.getState().scheduleData?.sundayScheduleTime2End
        }
      }

      return scheduleDayObj
    },
    setSchedule: (
      day,
      status,
      note,
      timeOneStart,
      timeOneEnd,
      timeTwoStart,
      timeTwoEnd
    ) => {
      if (day === 1) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            mondayScheduleValue: status,
            mondayScheduleNAComment: note?.trim() || null,
            mondayScheduleTime1Start: timeOneStart,
            mondayScheduleTime1End: timeOneEnd,
            mondayScheduleTime2Start: timeTwoStart,
            mondayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 2) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            tuesdayScheduleValue: status,
            tuesdayScheduleNAComment: note?.trim() || null,
            tuesdayScheduleTime1Start: timeOneStart,
            tuesdayScheduleTime1End: timeOneEnd,
            tuesdayScheduleTime2Start: timeTwoStart,
            tuesdayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 3) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            wednesdayScheduleValue: status,
            wednesdayScheduleNAComment: note?.trim() || null,
            wednesdayScheduleTime1Start: timeOneStart,
            wednesdayScheduleTime1End: timeOneEnd,
            wednesdayScheduleTime2Start: timeTwoStart,
            wednesdayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 4) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            thursdayScheduleValue: status,
            thursdayScheduleNAComment: note?.trim() || null,
            thursdayScheduleTime1Start: timeOneStart,
            thursdayScheduleTime1End: timeOneEnd,
            thursdayScheduleTime2Start: timeTwoStart,
            thursdayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 5) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            fridayScheduleValue: status,
            fridayScheduleNAComment: note?.trim() || null,
            fridayScheduleTime1Start: timeOneStart,
            fridayScheduleTime1End: timeOneEnd,
            fridayScheduleTime2Start: timeTwoStart,
            fridayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 6) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            saturdayScheduleValue: status,
            saturdayScheduleNAComment: note?.trim() || null,
            saturdayScheduleTime1Start: timeOneStart,
            saturdayScheduleTime1End: timeOneEnd,
            saturdayScheduleTime2Start: timeTwoStart,
            saturdayScheduleTime2End: timeTwoEnd
          }
        }))
      } else if (day === 0) {
        set((state) => ({
          ...state,
          scheduleData: {
            ...useScheduleStore.getState().scheduleData,
            sundayScheduleValue: status,
            sundayScheduleNAComment: note?.trim() || null,
            sundayScheduleTime1Start: timeOneStart,
            sundayScheduleTime1End: timeOneEnd,
            sundayScheduleTime2Start: timeTwoStart,
            sundayScheduleTime2End: timeTwoEnd
          }
        }))
      }
    },
    fetchData: async (id) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const _scheduleData = await scheduleService.fetchData(id)
        set((state) => ({ ...state, scheduleData: _scheduleData }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    updateData: async (id, feed) => {
      set((state) => ({ ...state, loading: true }))
      try {
        const _scheduleData = await scheduleService.updateData(id, feed)
        set((state) => ({ ...state, scheduleData: _scheduleData }))

        // fetch home data
        useHomeStore.getState().fetchData(id)
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    }
  }))
)

export { useScheduleStore }
